import { Signal } from '@angular/core';
import { IDatiqSelect } from '@dat/datiq-components-pkg/select';
import { DatiqGeoSuggestionSchema, IqRulesDataSchema } from '@dat/iq-proxy';
import { IqCssClassnames, IqCssClassNamesRule } from '@dat/iq-style-processor';
import { TabSchema } from '@dat/iq-control-tab';
import { z } from 'zod';

export const AddClassPayloadSchema = z.object({
    selector: z.string().optional(),
    classNames: z.array(z.string().nullable()),
});
export const RemoveClassPayloadSchema = z.object({
    selector: z.string().optional(),
    classNames: z.array(z.string().nullable()),
});
export const ToggleClassPayloadSchema = z.object({
    selector: z.string().optional(),
    classNames: z.array(z.string().nullable()),
});

export type AddClassPayload = z.infer<typeof AddClassPayloadSchema>;
export type RemoveClassPayload = z.infer<typeof RemoveClassPayloadSchema>;
export type ToggleClassPayload = z.infer<typeof ToggleClassPayloadSchema>;

/* Types */
export type ControlTypes = z.infer<typeof ControlTypesSchema>;
export type LegacyTypes = z.infer<typeof LegacyTypesSchema>;
export type TextControl = z.infer<typeof TextControlSchema>;
export type NumberControl = z.infer<typeof NumberControlSchema>;
export type IntegerControl = z.infer<typeof IntegerControlSchema>;
export type SelectControl =
    | z.infer<typeof SelectControlSchema>
    | { input: IDatiqSelect };
export type Option = z.infer<typeof OptionSchema>;
export type CurrencyControl = z.infer<typeof CurrencyControlSchema> & {
    classes: IqCssClassnames;
    rules?: { [key: string]: any; type: string }[];
    ruleOperator?: 'AND' | 'OR' | 'NOT';
};
export type CheckboxControl = z.infer<typeof CheckboxControlSchema> & {};
export type GeoControl = z.infer<typeof GeoControlSchema>;
export type LabelControl = z.infer<typeof LabelControlSchema>;
export type ActionControl = z.infer<typeof ActionControlSchema>;
export type ResetControl = z.infer<typeof ResetControlSchema>;
export type SubmitControl = z.infer<typeof SubmitControlSchema>;
export type PanelControl = z.infer<typeof PanelControlSchema> &
    Partial<{
        rules?: { [key: string]: any; type: string }[];
        ruleOperator?: 'AND' | 'OR' | 'NOT';
        classes: IqCssClassnames;
    }>;
export type TabsControl = z.infer<typeof TabsControlSchema>;
export type PercentControl = z.infer<typeof PercentControlSchema>;
type DerivedControl = z.infer<typeof ControlSchema>;
export type Control = DerivedControl &
    Partial<{
        rules: { [key: string]: any }[];
        classes?: IqCssClassnames;
        cssClassRules: IqCssClassNamesRule[];
    }>;
export type RVSearchBarODTracking = z.infer<typeof RvSearchBarODTrackingSchema>;
/* Schemas */
export const ControlTypesSchema = z.union([
    z.literal('Text'),
    z.literal('Number'),
    z.literal('Integer'),
    z.literal('Select'),
    z.literal('Currency'),
    z.literal('Percent'),
    z.literal('Checkbox'),
    z.literal('Geo'),
    z.literal('Label'),
    z.literal('Action'),
    z.literal('Reset'),
    z.literal('Submit'),
    z.literal('Panel'),
    z.literal('Tabs'),
]);

export const LegacyTypesSchema = z.union([
    z.literal('Select'),
    z.literal('Checkbox'),
    z.literal('Tabs'),
]);

type BaseControlType = Partial<{
    rules?: { [key: string]: any; type: string }[];
    ruleOperator?: 'AND' | 'OR' | 'NOT';
    classes: IqCssClassnames;
}>;

export const RvSearchBarODTrackingSchema = z.object({
    rateType: z.string(),
    origin: z.string(),
    destination: z.string(),
    equipType: z.string(),
    fuelSurchargeRate: z.string(),
    fuelSurchargeType: z.string(),
    rateDisplay: z.string(),
    searchId: z.string(),
});

// const BaseControlSchema = z.from<BaseControlType>();

const SharedControlSchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    testId: z.string().optional(),
    disabled: z.boolean().optional(),
    name: z.string(),
    required: z.boolean().optional(), // TODO: optional??
    type: ControlTypesSchema,
});

export const TextControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Text'),
        label: z.string(),
        value: z.string().optional(),
    }),
);

export const NumberControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Number'),
        value: z.number().optional(),
    }),
);

export const IntegerControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Integer'),
        value: z.number().optional(),
        minValue: z.number().optional(),
        maxValue: z.number().optional(),
        placeholder: z.string().optional(),
    }),
);

export const OptionSchema = z.object({
    id: z.string(),
    value: z.string(),
    display: z.string(),
    data: z.any(),
    props: z.any(),
    disabled: z.boolean(),
});

export const OptionSchemaValue = z.object({
    value: z.string().or(z.number()),
});

export const SelectControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Select'),
        options: z.array(z.custom<Option>()),
        required: z.boolean().optional(),
        value: OptionSchema.nullable(),
        style: z.union([z.literal('standard'), z.literal('thin')]),
        customErrorMessage: z.string().optional(),
        locationErrorMessage: z.string().optional(),
        multi: z.boolean().optional(),
    }),
);

export const CurrencyControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Currency'),
        minValue: z.number().optional(),
        maxValue: z.number().optional(),
        required: z.boolean().optional(),
        prefix: z.string().optional(),
        suffix: z.string().optional(),
        value: z.number().optional(),
    }),
);

export const CheckboxControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Checkbox'),
        value: z.boolean().optional(),
        checkedLabel: z.string(),
        uncheckedLabel: z.string(),
        disabledLabel: z.string().optional(),
        tooltipBody: z.string().optional(),
        tooltipHeader: z.string().optional(),
        tooltipLabel: z.string().optional(),
    }),
);

export const GeoControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Geo'),
        required: z.boolean().optional(),
        // value: z.object({
        //     displayName: z.string(),
        //     lat: z.number(),
        //     lon: z.number(),
        // }),
        // value: DatiqGeoSuggestionSchema,
        value: z.string(),
        customErrorMessage: z.string().optional(),
        startFocused: z.boolean(),
        locationErrorMessage: z.string().optional(),
    }),
);

export const LabelControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Label'),
    }),
);

export const ActionControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Action'),
        icon: z.string().optional(),
        value: z.any().optional(),
    }),
);

export const ResetControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Reset'),
        icon: z.string().optional(),
        initialData: z.any().optional(),
        disable: z.boolean().optional(),
    }),
);

export const SubmitControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Submit'),
        icon: z.string().optional(),
    }),
);

export const TabsControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Tabs'),
        tabs: z.array(TabSchema),
        value: OptionSchema.nullable(),
    }),
);

export const PercentControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Percent'),
        value: z.number().optional(),
        minValue: z.number().optional(),
        maxValue: z.number().optional(),
        placeholder: z.string().optional(),
    }),
);

export const PanelControlTypesSchema = z.discriminatedUnion('type', [
    TextControlSchema,
    NumberControlSchema,
    IntegerControlSchema,
    SelectControlSchema,
    CurrencyControlSchema,
    CheckboxControlSchema,
    GeoControlSchema,
    LabelControlSchema,
    ActionControlSchema,
    ResetControlSchema,
    SubmitControlSchema,
    TabsControlSchema,
    PercentControlSchema,
]);

export type PanelControlTypes = z.infer<typeof PanelControlTypesSchema> & {};

export const PanelControlSchema = SharedControlSchema.merge(
    z.object({
        type: z.literal('Panel'),
        controls: z.array(PanelControlTypesSchema),
        openLabel: z.string(),
        closedLabel: z.string(),
        disabledLabel: z.string(),
        isDisabled: z.boolean(),
        isEdited: z.boolean(),
        formClasses: z
            .record(z.array(IqRulesDataSchema(z.array(z.string()))))
            .optional(),
        panelClasses: z
            .record(z.array(IqRulesDataSchema(z.array(z.string()))))
            .optional(),
    }),
);

export const ControlSchema = z.discriminatedUnion('type', [
    TextControlSchema,
    NumberControlSchema,
    IntegerControlSchema,
    SelectControlSchema,
    CurrencyControlSchema,
    CheckboxControlSchema,
    GeoControlSchema,
    LabelControlSchema,
    ActionControlSchema,
    ResetControlSchema,
    SubmitControlSchema,
    PanelControlSchema,
    TabsControlSchema,
    PercentControlSchema,
]);

/* Helper Functions */
export function isOption(option: any): option is Option {
    return OptionSchema.safeParse(option).success;
}

export function isTabsControl(control: any): control is TabsControl {
    return TabsControlSchema.safeParse(control).success;
}

export function isControl(control: any): control is Control {
    return ControlSchema.safeParse(control).success;
}

export const MapperSchema = z.object({
    map: z.function().args(ControlSchema).returns(z.any()),
});

export type Mapper = z.infer<typeof MapperSchema>;

export interface IIqControlsStateService {
    controlState(control: Control): Signal<Control>;
}

const nonValueControlTypes: ControlTypes[] = [
    'Label',
    'Action',
    'Submit',
    'Reset',
];

export function isNonValueType(control: Control): boolean {
    return nonValueControlTypes.some((type) => control.type === type);
}
